import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { getAlerts } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { Card, CardHeader, CardTitle, CardContent, LoadingState, SEO } from '@/components';
import { ArrowLeft, AlertTriangle, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import type { Alert, MichiganTimezone } from '@miweather/shared';

// Default timezone for statewide alerts (most of Michigan is Eastern)
const DEFAULT_TIMEZONE: MichiganTimezone = 'America/Detroit';

type SeverityFilter = 'all' | 'extreme' | 'severe' | 'moderate' | 'minor';

export function AlertsPage() {
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.alerts.all,
    queryFn: getAlerts,
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <LoadingState message="Loading alerts..." />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-[var(--color-error)] mb-4">
          Failed to load alerts
        </h2>
        <p className="text-[var(--color-muted-foreground)]">
          {error instanceof Error ? error.message : 'An unexpected error occurred'}
        </p>
      </div>
    );
  }

  const alerts = data?.alerts || [];
  const filteredAlerts =
    severityFilter === 'all'
      ? alerts
      : alerts.filter((a) => a.severity === severityFilter);

  const severityCounts = {
    extreme: alerts.filter((a) => a.severity === 'extreme').length,
    severe: alerts.filter((a) => a.severity === 'severe').length,
    moderate: alerts.filter((a) => a.severity === 'moderate').length,
    minor: alerts.filter((a) => a.severity === 'minor').length,
  };

  const alertDescription = alerts.length > 0
    ? `${alerts.length} active weather alert${alerts.length !== 1 ? 's' : ''} in Michigan. View extreme, severe, moderate, and minor alerts from the National Weather Service.`
    : 'No active weather alerts in Michigan. Check current conditions and forecasts.';

  return (
    <div className="space-y-6">
      <SEO
        title="Weather Alerts"
        description={alertDescription}
        path="/alerts"
      />
      {/* Back link */}
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[var(--color-muted-foreground)] hover:text-white transition-colors text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Weather Alerts</h1>
          <p className="text-[var(--color-muted-foreground)]">
            {alerts.length} active alert{alerts.length !== 1 ? 's' : ''} in Michigan
          </p>
        </div>
        <div className="text-sm text-[var(--color-muted-foreground)] flex items-center gap-2 bg-[var(--color-card)] px-3 py-2 rounded-full">
          <Clock className="w-4 h-4" />
          Updated: {data?.updatedAt ? new Date(data.updatedAt).toLocaleTimeString('en-US', { timeZone: DEFAULT_TIMEZONE }) : 'N/A'} ET
        </div>
      </div>

      {/* Severity Filter */}
      <div className="flex flex-wrap gap-2">
        <FilterButton
          active={severityFilter === 'all'}
          onClick={() => setSeverityFilter('all')}
          count={alerts.length}
        >
          All
        </FilterButton>
        <FilterButton
          active={severityFilter === 'extreme'}
          onClick={() => setSeverityFilter('extreme')}
          count={severityCounts.extreme}
          severity="extreme"
        >
          Extreme
        </FilterButton>
        <FilterButton
          active={severityFilter === 'severe'}
          onClick={() => setSeverityFilter('severe')}
          count={severityCounts.severe}
          severity="severe"
        >
          Severe
        </FilterButton>
        <FilterButton
          active={severityFilter === 'moderate'}
          onClick={() => setSeverityFilter('moderate')}
          count={severityCounts.moderate}
          severity="moderate"
        >
          Moderate
        </FilterButton>
        <FilterButton
          active={severityFilter === 'minor'}
          onClick={() => setSeverityFilter('minor')}
          count={severityCounts.minor}
          severity="minor"
        >
          Minor
        </FilterButton>
      </div>

      {/* Alerts List */}
      {filteredAlerts.length === 0 ? (
        <Card variant="solid">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-[var(--color-muted-foreground)] mb-4" />
            <p className="text-xl text-[var(--color-muted-foreground)]">
              {severityFilter === 'all'
                ? 'No active weather alerts for Michigan'
                : `No ${severityFilter} alerts active`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAlerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterButton({
  children,
  active,
  onClick,
  count,
  severity,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
  count: number;
  severity?: 'extreme' | 'severe' | 'moderate' | 'minor';
}) {
  const severityColors = {
    extreme: 'bg-[var(--color-alert-extreme)]',
    severe: 'bg-[var(--color-alert-severe)]',
    moderate: 'bg-[var(--color-alert-moderate)]',
    minor: 'bg-[var(--color-alert-minor)]',
  };

  return (
    <button
      onClick={onClick}
      className={`
        px-4 py-2 rounded-full text-sm font-medium transition-all
        ${active
          ? severity
            ? `${severityColors[severity]} text-white`
            : 'bg-[var(--color-info)] text-white'
          : 'bg-[var(--color-card)] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]'
        }
      `}
    >
      {children} ({count})
    </button>
  );
}

function AlertCard({ alert }: { alert: Alert }) {
  const [expanded, setExpanded] = useState(false);

  const severityColors = {
    extreme: 'border-l-[var(--color-alert-extreme)]',
    severe: 'border-l-[var(--color-alert-severe)]',
    moderate: 'border-l-[var(--color-alert-moderate)]',
    minor: 'border-l-[var(--color-alert-minor)]',
  };

  const severityBadgeColors = {
    extreme: 'bg-[var(--color-alert-extreme)]',
    severe: 'bg-[var(--color-alert-severe)]',
    moderate: 'bg-[var(--color-alert-moderate)]',
    minor: 'bg-[var(--color-alert-minor)] text-black',
  };

  return (
    <Card variant="solid" className={`border-l-4 ${severityColors[alert.severity]}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${severityBadgeColors[alert.severity]}`}>
                {alert.severity}
              </span>
              <span className="px-2 py-1 rounded text-xs font-medium bg-[var(--color-muted)] capitalize">
                {alert.type}
              </span>
            </div>
            <CardTitle className="text-xl">{alert.event}</CardTitle>
          </div>
          <div className="text-right text-sm text-[var(--color-muted-foreground)]">
            <p>Until</p>
            <p className="font-medium text-white">{new Date(alert.expires).toLocaleDateString('en-US', { timeZone: DEFAULT_TIMEZONE })}</p>
            <p className="font-medium text-white">{new Date(alert.expires).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: DEFAULT_TIMEZONE })} ET</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-[var(--color-muted-foreground)] mb-4">{alert.headline}</p>

        <div className="mb-4">
          <p className="text-sm font-medium mb-2">Affected Areas:</p>
          <div className="flex flex-wrap gap-2">
            {alert.areas.slice(0, 5).map((area, i) => (
              <span key={i} className="text-xs bg-[var(--color-muted)] px-2 py-1 rounded">
                {area}
              </span>
            ))}
            {alert.areas.length > 5 && (
              <span className="text-xs text-[var(--color-muted-foreground)]">
                +{alert.areas.length - 5} more
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm text-[var(--color-info)] hover:text-[var(--color-accent)] transition-colors"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Hide details
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show details
            </>
          )}
        </button>

        {expanded && (
          <div className="mt-4 p-4 bg-[var(--color-background)] rounded-[var(--radius-lg)] border border-[var(--color-border)]">
            <p className="text-sm whitespace-pre-wrap text-[var(--color-muted-foreground)]">{alert.description}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
